import { useState, useCallback } from 'react';

const STORAGE_KEY = 'fintrack:show-item-count';

/**
 * useItemCountPreference — localStorage-backed toggle for item count display.
 *
 * When `showItemCount` is false, item count badges and KPI cards
 * are hidden throughout the app (detail sheet, transaction rows, insights).
 *
 * Default: `true` (new users see item counts).
 */
export function useItemCountPreference() {
  const [showItemCount, setShowItemCount] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) === true : true;
    } catch {
      return true;
    }
  });

  const toggleItemCount = useCallback(() => {
    setShowItemCount((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setItemCount = useCallback((value: boolean) => {
    setShowItemCount(value);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }, []);

  return { showItemCount, toggleItemCount, setItemCount } as const;
}
